/**
 * FocusMode - Modo de enfoque "No molestar"
 *
 * Diseñado para neurodivergencia:
 * - Elimina distracciones visuales
 * - Muestra solo el protocolo actual
 * - Facil de activar y desactivar
 *
 * Basado en WCAG: "Dar opcion de pausar, detener u ocultar contenido distractor"
 */

import { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Focus, CheckCircle2, Zap } from 'lucide-react';
import { cn } from '@/core/utils';
import { Button, Card } from '@/ui/primitives';
import { useUIStore, useProtocolStore } from '@/store';
import { ProtocolCard } from '@/features/protocols/components/ProtocolCard';
import { feedbackSuccess } from '@/services/feedback';

export function FocusMode() {
  const focusMode = useUIStore((state) => state.focusMode);
  const focusProtocolId = useUIStore((state) => state.focusProtocolId);
  const setFocusMode = useUIStore((state) => state.setFocusMode);
  const protocols = useProtocolStore((state) => state.protocols);

  // Cerrar con ESC
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && focusMode) {
        setFocusMode(false);
        feedbackSuccess();
      }
    },
    [focusMode, setFocusMode]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Obtener el protocolo enfocado
  const focusedProtocol = focusProtocolId
    ? protocols.find((p) => p.id === focusProtocolId)
    : null;

  // Si no hay protocolo específico, obtener el primero no completado
  const activeProtocol = focusedProtocol ?? protocols.find((p) =>
    p.subtasks.some((s) => !s.isDone)
  );

  const handleClose = () => {
    setFocusMode(false);
    feedbackSuccess();
  };

  // Calcular progreso del protocolo actual
  const getProgress = () => {
    if (!activeProtocol) return { completed: 0, total: 0, percentage: 0 };
    const completed = activeProtocol.subtasks.filter((s) => s.isDone).length;
    const total = activeProtocol.subtasks.length;
    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  };

  const progress = getProgress();
  const isCompleted = progress.completed === progress.total && progress.total > 0;

  return (
    <AnimatePresence>
      {focusMode && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-slate-950"
        >
          {/* Background pattern sutil */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-transparent pointer-events-none" />

          {/* Header minimo */}
          <div className="absolute top-4 left-0 right-0 px-6 py-4 flex items-center justify-between safe-top">
            <div className="flex items-center gap-2">
              <Focus className="w-5 h-5 text-blue-400" />
              <span className="text-sm font-medium text-slate-400">Modo Focus</span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="text-slate-400 hover:text-slate-200"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Contenido principal */}
          <div className="h-full flex flex-col items-center justify-center px-4 pb-8 pt-16">
            {activeProtocol ? (
              <div className="w-full max-w-md space-y-6">
                {/* Mensaje motivacional */}
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="text-center"
                >
                  <p className="text-sm text-slate-500">
                    {isCompleted
                      ? 'Excelente trabajo'
                      : 'Una cosa a la vez'}
                  </p>
                  <h2 className="text-xl font-semibold text-slate-200 mt-1">
                    {activeProtocol.title}
                  </h2>
                </motion.div>

                {/* Progress mini */}
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center justify-center gap-3"
                >
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-400" />
                    <span className="text-sm text-slate-400">
                      {progress.completed} de {progress.total}
                    </span>
                  </div>
                  <div className="h-2 w-32 bg-slate-800 rounded-full overflow-hidden">
                    <motion.div
                      className={cn(
                        'h-full rounded-full',
                        isCompleted ? 'bg-emerald-500' : 'bg-blue-500'
                      )}
                      initial={{ width: 0 }}
                      animate={{ width: `${progress.percentage}%` }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                    />
                  </div>
                  <span className="text-sm font-medium text-slate-300">
                    {progress.percentage}%
                  </span>
                </motion.div>

                {/* Protocolo card */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <ProtocolCard protocol={activeProtocol} />
                </motion.div>

                {/* Completado message */}
                {isCompleted && (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-center"
                  >
                    <Card className="p-6 bg-emerald-900/20 border-emerald-700/50">
                      <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                      <p className="text-emerald-300 font-medium">
                        Protocolo completado
                      </p>
                      <p className="text-sm text-emerald-400/70 mt-1">
                        Puedes salir del modo focus o continuar con otro protocolo.
                      </p>
                      <Button
                        variant="primary"
                        size="md"
                        onClick={handleClose}
                        className="mt-4"
                      >
                        Salir del modo focus
                      </Button>
                    </Card>
                  </motion.div>
                )}
              </div>
            ) : (
              /* Sin protocolos pendientes */
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center max-w-sm"
              >
                <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-200">
                  Todo completado
                </h2>
                <p className="text-slate-400 mt-2">
                  No hay protocolos pendientes. Buen trabajo.
                </p>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleClose}
                  className="mt-6"
                >
                  Salir del modo focus
                </Button>
              </motion.div>
            )}
          </div>

          {/* Hint de salida */}
          <div className="absolute bottom-4 left-0 right-0 text-center safe-bottom">
            <p className="text-xs text-slate-600">
              Presiona ESC o el boton X para salir
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Boton para activar modo focus desde cualquier protocolo
 */
interface FocusButtonProps {
  protocolId: string;
  className?: string;
}

export function FocusButton({ protocolId, className }: FocusButtonProps) {
  const setFocusMode = useUIStore((state) => state.setFocusMode);

  const handleClick = () => {
    setFocusMode(true, protocolId);
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        'p-2 rounded-lg transition-colors',
        'text-slate-500 hover:text-blue-400 hover:bg-blue-900/20',
        className
      )}
      title="Modo focus"
    >
      <Focus className="w-4 h-4" />
    </button>
  );
}
